<template>
  <v-dialog
    :value="visiblePopup"
    :max-width="1200"
    scrollable
    @click:outside="onCloseClickButton"
    @keydown.esc="onCloseClickButton"
  >
    <v-card class="sitemap-popup popup-wrapper">
      <div class='close-button-section'>
        <span class='close_btn' @click='onCloseClickButton'>
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>
      <div class='sitemap-popup__title'> {{ this.$t('sitemap.popup.title') }} </div>
      <div class='sitemap-popup__subtitles'>
        <div class='sitemap-popup__subtitles__subtitle'>
          <div class='sitemap-popup__subtitles__subtitle__bold'>{{this.$t('sitemap.popup.subtitle')}}</div>
          <div>{{this.website}}</div>
        </div>
        <div class='sitemap-popup__subtitles__subtitle' v-if='displayFeatures'>
          <div class='sitemap-popup__subtitles__subtitle__bold'>{{this.$t('sitemap.popup.subtitle.features')}}</div>
          <div
            v-for='(feature, index) in featuresFormatted'
            :key='index' class='sitemap-popup__subtitles__subtitle__feature-section' :class="{'unfound-link': checkFeatureContainUniqueLink(feature)}">
            <div @click="featureClicked(feature)" class='feature' :class="{'unfound-link-item': checkFeatureContainUniqueLink(feature)}">
              {{ $t(`feature.title.${feature}`) }}
            </div>
            <div class='sitemap-popup__subtitles__subtitle__feature-section__link-section'>
              <v-btn v-for='(link, key) in uniqueLinksPerFeature[feature]'
                     :key="key" class="open-btn" :href="link" target="_blank" :ripple="false" @click.stop="" :title="link">
                <v-icon>
                  open_in_new
                </v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>
      <v-card-text>
        <div class='sitemap-popup__content'>
          <collapsible-tidy-tree
            :data='data'
            :featureLinks="clickedFeatureLinks"
            :existFeatureLinks="linksExistsInSitemapPerFeature"
          />
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import CollapsibleTidyTree from '@/components/charts/CollapsibleTidyTree.vue';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'SitemapPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: { CollapsibleTidyTree },
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
    features: {
      type: Object,
      required: false,
      default: () => {},
    },
    data: {
      type: Object,
      required: true,
    },
    website: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      updated: false,
      clickedFeatureLinks: [],
    };
  },
  computed: {
    isUpdated() {
      return this.updated;
    },
    featuresFormatted() {
      return Object.keys(this.features).filter(featureKey => this.features[featureKey] === true) || [];
    },
    displayFeatures() {
      return this.featuresFormatted.length > 0;
    },
    linksExistsInSitemapPerFeature() {
      const linksPerFeature = {};
      const categories = this.featuresFormatted;
      categories.forEach(category => {
        const links = this.features[`${category}Evidence`];
        const filteredFeatureList = links.filter(feature => {
          const newLink = feature.replace(this.data.link, '');
          const parts = newLink.split('/').filter(item => item.length);
          for (let i = 0; i < parts.length; i += 1) {
            if (!this.findStringInChildren(this.data.children, parts[i])) {
              return false;
            }
          }
          return true;
        });
        linksPerFeature[category] = filteredFeatureList;
      });
      return linksPerFeature;
    },
    uniqueLinksPerFeature() {
      const linksPerFeature = {};
      const categories = this.featuresFormatted;
      categories.forEach(category => {
        const links = this.features[`${category}Evidence`];
        const filteredFeatureList = links.filter(feature => {
          const newLink = feature.replace(this.data.link, '');
          const parts = newLink.split('/').filter(item => item.length);
          for (let i = 0; i < parts.length; i += 1) {
            if (!this.findStringInChildren(this.data.children, parts[i])) {
              return true;
            }
          }
          return false;
        });
        linksPerFeature[category] = filteredFeatureList;
      });
      return linksPerFeature;
    },
  },
  methods: {
    async onCloseClickButton() {
      this.clickedFeatureLinks = [];
      await this.$emit('close');
    },
    featureClicked(category) {
      this.clickedFeatureLinks = this.linksExistsInSitemapPerFeature[category];
    },
    checkFeatureContainUniqueLink(category) {
      return this.uniqueLinksPerFeature[category].length > 0;
    },
    findStringInChildren(objects, searchString) {
      let result = false;
      for (let i = 0; i < objects.length; i += 1) {
        if (objects[i].name === searchString) {
          result = true;
        }
        if (objects[i].children) {
          const foundInChildren = this.findStringInChildren(objects[i].children, searchString);
          if (foundInChildren) {
            result = true;
          }
        }
      }
      return result;
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';
.sitemap-popup {
  &.popup-wrapper {
    max-width: rem(1200);
    display: flex;
    flex-direction: column;
    .close-button-section {
      z-index: 2;
      padding-top: rem(15);
      padding-right: rem(15);
      .close_btn {
        float: right;
        &:hover {
          cursor: pointer;
          color: var(--brand-primary);
        }
      }
    }
  }
  &__title {
    margin-top: rem(-10);
    padding-bottom: rem(20);
    padding-left: rem(35);
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: rem(24);
    font-weight: bold;
    line-height: rem(20);
    letter-spacing: 0;
  }
  &__subtitles {
    display: flex;
    flex-direction: row;
    padding-right: rem(35);
    &__subtitle {
      display: flex;
      flex-direction: row;
      padding-bottom: rem(20);
      padding-left: rem(35);
      color: var(--text-secondary);
      font-family: $font-family-medium;
      text-transform: uppercase;
      font-size: rem(14);
      line-height: rem(20);
      letter-spacing: 0;
      &__bold{
        color: var(--text-primary);
        font-family: $font-family-semibold;
        padding-right: rem(10);
      }
      &__feature-section {
        display: flex;
        justify-content: center;
        height: rem(21);
        border-radius: $radius-md;
        background: var(--bg-tertiary);
        padding: rem(2) rem(6);
        text-align: center;
        margin-right: rem(12);
        &:hover {
          cursor: pointer;
        }
        .feature {
          color: var(--text-secondary);
          font-family: $font-family-regular;
          font-size: rem(14);
          letter-spacing: 0;
          line-height: rem(17);
          max-width: rem(130);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-transform: capitalize;
        }
        .unfound-link-item {
          color: var(--severity-critical);
          background-color: var(--severity-critical-bg);
          padding: rem(2) rem(6);
          height: rem(22);
          border-radius: $radius-md;
          margin-top: rem(-3);
          cursor: default;
        }
        &__link-section {
          display: flex;
          justify-content: space-between;
          margin-right: rem(-9);
          margin-left: rem(9);
          .open-btn {
            padding: rem(2);
            color: var(--text-secondary);
            background-color: transparent;
            box-shadow: none;
            min-width: rem(20);
            max-width: rem(20);
            min-height: rem(16);
            max-height: rem(16);
            .v-icon {
              font-size: rem(16);
            }
            &:hover {
              cursor: pointer;
              background-color: var(--bg-hover);
              border-radius: $radius-sm;
            }
          }
        }
      }
      .unfound-link {
        background-color: transparent;
      }
    }
  }
  &__content {
    margin-top: rem(9);
    min-width: rem(500);
    min-height: rem(700);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
}

</style>
